import cn from 'classnames'
import * as React from 'react'
import { IconCell } from '../icon-cell'
import { Typography } from '../typography'
import styles from './Breadcrumbs.module.css'
import { BreadcrumbsItemProps, BreadcrumbsProps } from './Breadcrumbs.types'

const BreadcrumbSeparator: React.FC<{
  isLast: boolean
  showOn: 'mobile' | 'desktop'
}> = ({ isLast, showOn }) => {
  if (isLast)
    return null

  return (
    <IconCell
      glyph={showOn === 'mobile' ? 'ChevronLeft' : 'ChevronRightExtraSmall'}
      size="sm"
      className={showOn === 'mobile' ? styles.separatorMobile : styles.separatorDesktop}
    />
  )
}

function renderItems(items: BreadcrumbsItemProps[]): React.ReactNode {
  const penultimateIdx = items.length - 2
  const lastIdx = items.length - 1

  return items.map((item, idx) => {
    const isPenultimate = idx === penultimateIdx
    const isLast = idx === lastIdx

    return (
      <li
        key={item.id}
        className={cn(styles.listItem, { [styles.listItemPenultimate]: isPenultimate })}
        aria-current={isLast ? 'page' : undefined}
        itemProp="itemListElement"
        itemScope
        itemType="https://schema.org/ListItem"
      >
        {item.href && !isLast
          ? (
              <a href={item.href} className={styles.link} itemProp="item">
                {isPenultimate && (
                  <BreadcrumbSeparator isLast={false} showOn="mobile" />
                )}
                <Typography variant="body-m-regular-tight" itemProp="name">{item.label}</Typography>
              </a>
            )
          : (
              <Typography variant="body-m-regular-tight" as="span" itemProp="name">
                {item.label}
              </Typography>
            )}
        <meta itemProp="position" content={String(idx + 1)} />
        <BreadcrumbSeparator isLast={isLast} showOn="desktop" />
      </li>
    )
  })
}

export function Breadcrumbs({ items, className }: BreadcrumbsProps) {
  return (
    <nav aria-label="Breadcrumb" className={cn(styles.root, className)}>
      <ol className={styles.list} itemScope itemType="https://schema.org/BreadcrumbList">
        {renderItems(items)}
      </ol>
    </nav>
  )
}

export default Breadcrumbs
